import { z } from "zod";

import { RepresentationRecord } from "./RepresentationRecord";

/**
 * Shadow of the CanonicalDataType enum in the roboto package.
 *
 * A member added here needs no matching edit elsewhere: the schema that
 * validates a stored data type derives its values from this enum via `z.enum`.
 */
export enum CanonicalDataType {
  Array = "array",
  Boolean = "boolean",
  Byte = "byte",
  Categorical = "categorical",
  Image = "image",
  Number = "number",
  NumberArray = "number_array",
  Object = "object",
  String = "string",
  Timestamp = "timestamp",
  Unknown = "unknown",

  // Special purpose types for data that represents geographic points
  LatDegFloat = "latdegfloat",
  LonDegFloat = "londegfloat",
  LatDegInt = "latdegint",
  LonDegInt = "londegint",
}

/**
 * Shadow of the MessagePathMetadataWellKnown enum in the roboto package.
 */
export enum MessagePathMetadataWellKnown {
  ColumnName = "column_name",
  Categories = "categories",
  Unit = "unit",
}

export interface MessagePathRecord {
  canonical_data_type: CanonicalDataType;
  created: string; // timestamp
  created_by: string;
  data_type: string;
  message_path: string;
  message_path_id: string;
  metadata: Record<string, unknown>;
  modified: string; // timestamp
  modified_by: string;
  /**
   * The field's location in the source schema, one entry per level of nesting.
   * Entry count and leading entries place the field in the topic tree, the last
   * entry labels and addresses it there, and the first entry is the column the
   * Parquet reader fetches. Entries hold verbatim source names, so a name that
   * itself contains a dot stays one level instead of splitting in two.
   */
  path_in_schema: string[];
  representations: RepresentationRecord[];
  /**
   * The field's full path in the source schema as one string. Read for display
   * only: tooltips, link and search-result text, and panel titles prefixed with
   * the topic name. Anything that addresses data or nests the field reads
   * `path_in_schema`, which is already split at the right boundaries.
   */
  source_path: string;
  topic_id: string;
}

export enum MessagePathPartType {
  Attr = "Attr",
  Slice = "Slice",
}

export interface MessagePathAttr {
  type: MessagePathPartType.Attr;
  attribute: string;
  dataType: CanonicalDataType;
}

export interface MessagePathSlice {
  type: MessagePathPartType.Slice;
  start: number;
  end?: number | null;
}

export type MessagePathPart = MessagePathAttr | MessagePathSlice;

export function isMessagePathAttr(part: unknown): part is MessagePathAttr {
  return (
    typeof part === "object" &&
    part !== null &&
    "type" in part &&
    part.type === MessagePathPartType.Attr
  );
}

/**
 * Data type of the field these parts address: the last attribute part's type,
 * or undefined for parts with no attribute at all. A trailing slice does not
 * narrow it, so a sliced array path reports the array type rather than the
 * element type.
 */
export function messagePathDataType(
  parts: MessagePathPart[],
): CanonicalDataType | undefined {
  return [...parts].reverse().find(isMessagePathAttr)?.dataType;
}

const messagePathAttrSchema = z.strictObject({
  type: z.literal(MessagePathPartType.Attr),
  attribute: z.string(),
  dataType: z.enum(CanonicalDataType),
});

const messagePathSliceSchema = z.strictObject({
  type: z.literal(MessagePathPartType.Slice),
  start: z.number(),
  // An open-ended slice is Infinity in memory and null once stored, since JSON
  // cannot express Infinity; both spellings mean open-ended. z.number() alone
  // rejects Infinity, so the literal has to be spelled out.
  end: z.number().or(z.literal(Infinity)).nullable().optional(),
});

/** Runtime validation for a {@link MessagePathPart} held in persisted state. */
export const messagePathPartSchema: z.ZodType<MessagePathPart> = z.union([
  messagePathAttrSchema,
  messagePathSliceSchema,
]);

export interface MessagePathRepresentationMapping {
  message_paths: MessagePathRecord[];
  representation: RepresentationRecord;
}
